import { User, UserFromAPI } from "../types";
import {
    LOGIN_ENDPOINT,
    LOGOUT_ENDPOINT,
    RESET_PASSWORD_ENDPOINT,
    USER_ENDPOINT,
    VERIFY_MFA_ENDPOINT,
} from "./endpoints";
import { fetcher } from "./utils";

type SimpleResponse = {
    success?: boolean;
    error?: string;
    error_code?: LoginErrorCode;
    message?: string;
};

type OTP_CHALLENGE = "EMAIL_OTP" | "SMS_MFA";

export type LoginErrorCode =
    | "reset-password-required"
    | "invalid-credentials"
    | "incorrect-code"
    | "password-previously-used"
    | "unexpected-error";

export const loginWithAPI = async ({
    email,
    password,
}: {
    email: string;
    password: string;
}): Promise<
    | (SimpleResponse & {
          challenge?: OTP_CHALLENGE;
          session?: string;
      })
    | null
> => {
    try {
        const response = await fetcher(
            LOGIN_ENDPOINT,
            {
                data: { username: email, password },
                method: "PUT",
            },
            true
        );

        return response;
    } catch (error) {
        console.error("Login error:", error);
        return { success: false };
    }
};

export const verifyMFA = async (username: string, code: string) => {
    const response = await fetcher(
        VERIFY_MFA_ENDPOINT,
        {
            method: "PUT",
            data: { username, code },
        },
        true
    );
    return response as SimpleResponse;
};

export const resetPassword = async (username: string, newPassword: string) => {
    try {
        const response = await fetcher(
            RESET_PASSWORD_ENDPOINT,
            {
                data: { username, password: newPassword },
                method: "PUT",
            },
            true
        );
        return response as
            | (SimpleResponse & { challenge: OTP_CHALLENGE; session: string })
            | null;
    } catch (error) {
        console.error("Reset password error:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
};

export const sendOtpCode = async (
    email: string,
    password: string,
    otpCode: string
) => {
    try {
        const response = await fetcher(LOGIN_ENDPOINT, {
            params: { username: email, password, otp_code: otpCode },
        });

        return response;
    } catch (error) {
        console.error("OTP code error:", error);
        return { success: false };
    }
};

export const logoutWithAPI = async () => {
    try {
        const successfulLogout = await fetcher(LOGOUT_ENDPOINT, {
            method: "PUT",
            keepalive: true,
        });

        return Boolean(successfulLogout);
    } catch (error) {
        console.error("Logout error:", error);
        return false;
    }
};

export const getCurrentUser = async () => {
    try {
        const response: UserFromAPI = await fetcher(USER_ENDPOINT, {});
        if (!response || !response.username) {
            return null;
        }

        const userData: User = {
            ...response,
            userName: response.username,
            blockedUIElements: response["blocked ui elements"] || [],
        };
        return userData;
    } catch (error) {
        console.error("Error fetching user:", error);
        return null;
    }
};
